/**
 * /well-servicing/jobs — the WS-2 job register (GET /well-servicing/jobs,
 * fieldiq-api#1282).
 *
 * Full width; a row opens the job on its own page. Filters (status, customer,
 * rig, search) live in the URL so Back returns to the list as it was. Every
 * filter is server-side. A field hand's list is the API's — only the jobs they
 * are on — and New job is offered to the dispatch roles only.
 *
 * The counts are a tally of the unfiltered list (no endpoint returns one).
 * Status chips come from the catalog (GET /meta), so a status label is the API's.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarClock, ClipboardList, Plus, Search, Truck } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWsMeta } from '../../hooks/useWellServicing';
import { useWsJobs } from '../../hooks/useWsJobs';
import { queryLoadFailure } from '../../lib/load-failure';
import { canDispatchWs, wsJobStatusSkin, WS_JOB_STATUS_SKIN } from '../../lib/well-servicing';
import type { WsJob } from '../../types/well-servicing';
import { useOpenRecord } from '../downhole/recordPage';
import { JobCard, jobColumns } from './JobBits';
import { WS_EYEBROW, WS_INPUT, WsChip, WsChipRow, WsEmpty, WsPanel, WsRegister } from './WsBits';

const ON_SITE = new Set(['moving_in', 'in_progress', 'rigging_out']);

export default function WsJobsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const rigId = searchParams.get('rigId') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const catalog = useWsMeta().data?.data ?? null;
  const filters = useMemo(() => ({ status, customerId, rigId, q: qParam }), [status, customerId, rigId, qParam]);
  const filtered = !!(status || customerId || rigId || qParam);
  const listQuery = useWsJobs(filters);
  const allQuery = useWsJobs();
  const jobs = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (j: WsJob) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canDispatchWs(user?.role);

  const customers = useMemo(() => {
    const seen = new Map<string, string>();
    (all ?? []).forEach((j) => seen.set(j.customerId, j.customerName ?? '—'));
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);
  const rigs = useMemo(() => {
    const seen = new Map<string, string>();
    (all ?? []).forEach((j) => j.rigId && seen.set(j.rigId, j.rigNumber ?? '—'));
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const statuses = catalog?.jobStatuses ?? Object.entries(WS_JOB_STATUS_SKIN).map(([value, s]) => ({ value, label: s.label }));

  return (
    <div data-testid="ws-jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title="Jobs"
        description="Service rig jobs — equipment, rent-ins, BOP stack and daily tour sheets."
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/well-servicing/jobs/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New job
            </button>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Jobs', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Scheduled', tone: 'info', loading: countsLoading, value: countOf((j) => j.status === 'scheduled') },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'On the well', sub: 'Moving in to rigging out', tone: 'accent', loading: countsLoading, value: countOf((j) => ON_SITE.has(j.status)) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WsChipRow label="Filter by status">
            <WsChip label="All" active={!status} onClick={() => setParam('status', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {statuses.map((s) => (
              <WsChip
                key={s.value}
                label={s.label}
                active={status === s.value}
                onClick={() => setParam('status', s.value)}
                count={countsLoading ? undefined : countOf((j) => j.status === s.value)}
              />
            ))}
          </WsChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_14rem_12rem]">
            <label className="relative block">
              <span className="sr-only">Search job, well, customer, PO or AFE</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search job, well, customer, PO or AFE"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={WS_INPUT} aria-label="Filter by customer" value={customerId} onChange={(e) => setParam('customerId', e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select className={WS_INPUT} aria-label="Filter by rig" value={rigId} onChange={(e) => setParam('rigId', e.target.value)}>
              <option value="">All rigs</option>
              {rigs.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          <WsPanel
            eyebrow="Job register"
            title="Well servicing jobs"
            testId="ws-job-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && jobs.length > 0
                ? filtered
                  ? `Showing ${jobs.length} of ${countOf(() => true)} jobs`
                  : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <WsRegister
              query={listQuery}
              rows={jobs}
              columns={jobColumns()}
              rail={(j) => wsJobStatusSkin(j.status).rail}
              onOpen={(j) => openRecord(`/well-servicing/jobs/${j.id}`)}
              subject="jobs"
              testId="ws-job"
              minWidth="min-w-[900px]"
              empty={filtered ? <WsEmpty>No job matches these filters.</WsEmpty> : <WsEmpty>There are no well servicing jobs yet.</WsEmpty>}
              card={(j) => <JobCard job={j} />}
            />
          </WsPanel>
        </div>
      </section>
    </div>
  );
}
